import React, { useState, useEffect } from 'react';
import { getAuditLog } from '../utils/api';

const ACTION_ICONS = {
  package_created: '◫',
  package_updated: '✎',
  lifecycle_transition: '→',
  items_assigned: '◈',
  approval_requested: '⏳',
  approval_approved: '✓',
  approval_rejected: '✕',
  rfq_sent: '✉',
  bid_leveling_run: '⚖',
  award_approved: '★',
  project_created: '⬡',
  project_updated: '✎',
};

const ACTION_COLORS = {
  approval_approved: 'var(--color-success)',
  approval_rejected: 'var(--color-error)',
  award_approved: 'var(--color-success)',
  lifecycle_transition: 'var(--color-teal)',
  default: 'var(--color-text-light)',
};

function formatAction(action) {
  if (action.startsWith('lifecycle_transition:')) {
    const parts = action.replace('lifecycle_transition:', '').split('→');
    return `Moved: ${parts[0]?.trim()} → ${parts[1]?.trim()}`;
  }
  return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(date) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export default function AuditTrail({ entityType, entityId, maxItems = 20 }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (entityType && entityId) loadLogs();
  }, [entityType, entityId]);

  async function loadLogs() {
    setLoading(true);
    try {
      const data = await getAuditLog(entityType, entityId);
      setLogs(data || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  if (loading) return <div style={{ padding: 16, color: 'var(--color-text-light)', fontSize: '0.875rem' }}>Loading activity...</div>;
  if (!logs.length) return <div style={{ padding: 16, color: 'var(--color-text-light)', fontSize: '0.875rem' }}>No activity yet</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {logs.slice(0, maxItems).map((log, i) => {
        const actionKey = Object.keys(ACTION_ICONS).find(k => log.action.startsWith(k)) || 'default';
        const icon = ACTION_ICONS[actionKey] || '●';
        const color = ACTION_COLORS[actionKey] || ACTION_COLORS.default;

        return (
          <div key={log.id} style={{
            display: 'flex', gap: 12, padding: '10px 0',
            borderBottom: i < logs.length - 1 ? '1px solid var(--color-border)' : 'none',
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'var(--color-background)',
              border: '1px solid var(--color-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.875rem', color, flexShrink: 0,
            }}>{icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text-dark)', marginBottom: 2 }}>
                {formatAction(log.action)}
              </div>
              {log.actor_name && (
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                  by {log.actor_name}
                </div>
              )}
              {log.metadata && Object.keys(log.metadata).length > 0 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                  {JSON.stringify(log.metadata).slice(0, 80)}
                </div>
              )}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)', flexShrink: 0, fontFamily: 'var(--font-mono)' }}>
              {timeAgo(log.created_at)}
            </div>
          </div>
        );
      })}
    </div>
  );
}